import { EmptyState } from '@today/ui-components'
import { Badge, Button } from '@today/ui-primitives'
import type { ReactElement, ReactNode } from 'react'
import { ORG_STATUS_VARIANT } from './org-tree.view'
import type { OrgUnitDetailView, Translate } from './org-types'

/**
 * Props for {@link OrgUnitDetailPaneView}.
 *
 * @property state - `none` when no unit is selected.
 * @property onEdit / onNewChild / onMove - Present only with `organization_unit.write`.
 * @property children - The Locations and Assigned users panels.
 */
export interface OrgUnitDetailPaneViewProps {
	t: Translate
	state: 'none' | 'loading' | 'notFound' | 'error' | 'ready'
	unit: OrgUnitDetailView | null
	onRetry: () => void
	onEdit?: () => void
	onNewChild?: () => void
	onMove?: () => void
	children?: ReactNode
}

const fieldRows = (t: Translate, unit: OrgUnitDetailView): Array<[string, ReactNode]> => {
	const text = (value: string | null) => value ?? t('unit.none')
	return [
		[
			'code',
			<span key="code" className="font-mono">
				{unit.code}
			</span>,
		],
		['name', unit.name],
		['shortName', text(unit.shortName)],
		['externalId', text(unit.externalId)],
		['typeCode', unit.typeLabel],
		['parent', unit.parentLabel ?? t('unit.root')],
		[
			'status',
			<Badge key="status" size="sm" variant={ORG_STATUS_VARIANT[unit.status]}>
				{t(`status.${unit.status}`)}
			</Badge>,
		],
		['isPhysical', t(unit.isPhysical ? 'unit.yes' : 'unit.no')],
		['institutionCode', text(unit.institutionCode)],
		['countryCode', text(unit.countryCode)],
		['timezone', text(unit.timezone)],
		['validFrom', text(unit.validFrom)],
		['validTo', text(unit.validTo)],
	]
}

function Actions({ props }: { props: OrgUnitDetailPaneViewProps }): ReactElement {
	const { t } = props
	return (
		<div className="flex gap-2">
			{props.onEdit ? (
				<Button size="sm" onPress={props.onEdit}>
					{t('unit.edit')}
				</Button>
			) : null}
			{props.onNewChild ? (
				<Button size="sm" variant="muted" onPress={props.onNewChild}>
					{t('unit.newChild')}
				</Button>
			) : null}
			{props.onMove ? (
				<Button size="sm" variant="muted" onPress={props.onMove}>
					{t('unit.moveTo')}
				</Button>
			) : null}
		</div>
	)
}

function Identity({ t, unit }: { t: Translate; unit: OrgUnitDetailView }): ReactElement {
	return (
		<dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-ds-sm">
			{fieldRows(t, unit).map(([key, value]) => (
				<div key={key} className="contents">
					<dt className="text-ds-text-muted">{t(`unit.fields.${key}`)}</dt>
					<dd>{value}</dd>
				</div>
			))}
			<dt className="text-ds-text-muted">{t('unit.fields.metadata')}</dt>
			<dd>{unit.metadata ? <pre className="text-ds-xs">{unit.metadata}</pre> : t('unit.none')}</dd>
		</dl>
	)
}

/** The selected unit: identity, actions, provenance placeholder and the nested panels (organism). */
export function OrgUnitDetailPaneView(props: OrgUnitDetailPaneViewProps): ReactElement {
	const { t, unit } = props
	if (props.state === 'none') return <EmptyState title={t('unit.selectPrompt')} />
	if (props.state === 'loading') return <p aria-live="polite">{t('tree.loading')}</p>
	if (props.state === 'notFound') return <EmptyState title={t('unit.notFound')} />
	if (props.state === 'error' || !unit) {
		return <EmptyState title={t('unit.loadError')} action={{ label: t('tree.retry'), onPress: props.onRetry }} />
	}
	return (
		<article aria-label={`${unit.code} · ${unit.name}`} className="flex flex-col gap-4">
			<header className="flex items-center justify-between gap-2">
				<h2 className="text-ds-lg font-semibold text-ds-text">{unit.name}</h2>
				<Actions props={props} />
			</header>
			<section aria-label={t('unit.identity')}>
				<Identity t={t} unit={unit} />
			</section>
			{props.children}
			<section aria-label={t('provenance.title')} className="flex flex-col gap-1">
				<h3 className="text-ds-base font-semibold">{t('provenance.title')}</h3>
				<p className="text-ds-sm text-ds-text-muted">{t('provenance.empty')}</p>
			</section>
		</article>
	)
}
